/**
 * Golfer Profile System
 *
 * Uses handicap as the primary skill proxy, with strokes-gained-inspired
 * categories to describe skill shape within a handicap bracket.
 *
 * This is skill-shaped probability modelling, not actual strokes gained
 * measurement (which would require shot-by-shot positional data).
 */

/**
 * Golfer profile schema
 *
 * @typedef {Object} GolferProfile
 * @property {string} id - Unique identifier
 * @property {string} name - Display name
 * @property {string} nationality - Country code (e.g., "USA", "GBR")
 * @property {number} handicapIndex - Overall ability (0 = scratch, negative = pro level)
 * @property {Object} skills - Skill modifiers relative to handicap baseline
 * @property {number} skills.offTheTee - Driving ability adjustment (-1 to +1)
 * @property {number} skills.approach - Iron play adjustment (-1 to +1)
 * @property {number} skills.aroundGreen - Short game adjustment (-1 to +1)
 * @property {number} skills.putting - Putting adjustment (-1 to +1)
 * @property {number} consistency - Variance multiplier (0.7 = consistent, 1.3 = volatile)
 * @property {Object} situational - Context-dependent modifiers
 */

/**
 * Create a new golfer profile with defaults
 */
export const createGolferProfile = (overrides = {}) => ({
    id: overrides.id || `golfer-${Date.now()}`,
    name: overrides.name || "New Golfer",
    nationality: overrides.nationality || "GBR",
    handicapIndex: overrides.handicapIndex ?? 15,

    skills: {
        offTheTee: 0,
        approach: 0,
        aroundGreen: 0,
        putting: 0,
        ...overrides.skills
    },

    consistency: overrides.consistency ?? 1.0,

    situational: {
        weatherResilience: 0.5,    // 0-1, higher = better in bad conditions
        pressureHandling: 0,       // -0.5 to +0.5, negative = chokes
        homeAdvantage: 0.2,        // Strokes gained when playing home country
        ...overrides.situational
    },

    // Hole-type adjustments (derived from skills, but can be overridden)
    holeTypeAdjustments: overrides.holeTypeAdjustments || null
});

/**
 * Calculate hole-type adjustments from skill profile
 * Players with strong OTT skills do better on par 5s
 * Players with strong approach/ARG do better on par 3s
 */
export const calculateHoleTypeAdjustments = (skills) => {
    return {
        par3: (skills.approach * 0.4 + skills.aroundGreen * 0.3 + skills.putting * 0.3) * 0.15,
        par4: 0, // Baseline - all skills matter equally
        par5: (skills.offTheTee * 0.5 + skills.approach * 0.3) * 0.2
    };
};

/**
 * Get effective hole-type adjustments for a golfer
 */
export const getHoleTypeAdjustments = (golfer) => {
    if (golfer.holeTypeAdjustments) {
        return golfer.holeTypeAdjustments;
    }
    return calculateHoleTypeAdjustments(golfer.skills);
};

/**
 * Preset golfer archetypes demonstrating different skill shapes
 */
export const GOLFER_PRESETS = {
    // Balanced scratch golfer
    scratch: createGolferProfile({
        id: "preset-scratch",
        name: "Scratch Player",
        handicapIndex: 0,
        consistency: 0.9,
        situational: {
            weatherResilience: 0.6,
            pressureHandling: 0.1,
            homeAdvantage: 0.2
        }
    }),

    // Long hitter who struggles on the greens
    bomber: createGolferProfile({
        id: "preset-bomber",
        name: "The Bomber",
        handicapIndex: 8,
        skills: {
            offTheTee: 0.6,
            approach: 0.2,
            aroundGreen: -0.2,
            putting: -0.3
        },
        consistency: 1.2,
        situational: {
            weatherResilience: 0.4,
            pressureHandling: -0.1,
            homeAdvantage: 0.2
        }
    }),

    // Accurate player who maximises scoring opportunities
    precision: createGolferProfile({
        id: "preset-precision",
        name: "The Precision Player",
        handicapIndex: 10,
        skills: {
            offTheTee: -0.2,
            approach: 0.5,
            aroundGreen: 0.3,
            putting: 0.2
        },
        consistency: 0.8,
        situational: {
            weatherResilience: 0.5,
            pressureHandling: 0.2,
            homeAdvantage: 0.2
        }
    }),

    // Excellent short game covers up other weaknesses
    shortGameWizard: createGolferProfile({
        id: "preset-short-game",
        name: "Short Game Wizard",
        handicapIndex: 12,
        skills: {
            offTheTee: -0.3,
            approach: -0.1,
            aroundGreen: 0.7,
            putting: 0.5
        },
        consistency: 0.85,
        situational: {
            weatherResilience: 0.6,
            pressureHandling: 0.15,
            homeAdvantage: 0.2
        }
    }),

    // Inconsistent player with high ceiling but low floor
    streaky: createGolferProfile({
        id: "preset-streaky",
        name: "The Streaky Player",
        handicapIndex: 14,
        skills: {
            offTheTee: 0.3,
            approach: 0.2,
            aroundGreen: 0.1,
            putting: 0.1
        },
        consistency: 1.4,
        situational: {
            weatherResilience: 0.3,
            pressureHandling: -0.2,
            homeAdvantage: 0.25
        }
    }),

    // High handicapper who's working on their game
    improving: createGolferProfile({
        id: "preset-improving",
        name: "The Improver",
        handicapIndex: 22,
        skills: {
            offTheTee: -0.1,
            approach: -0.2,
            aroundGreen: 0.1,
            putting: 0.2
        },
        consistency: 1.1,
        situational: {
            weatherResilience: 0.4,
            pressureHandling: 0,
            homeAdvantage: 0.3
        }
    }),

    // Links specialist
    linksPlayer: createGolferProfile({
        id: "preset-links",
        name: "Links Specialist",
        handicapIndex: 6,
        skills: {
            offTheTee: 0.2,
            approach: 0.3,
            aroundGreen: 0.4,
            putting: 0.1
        },
        consistency: 0.9,
        situational: {
            weatherResilience: 0.85,
            pressureHandling: 0.1,
            homeAdvantage: 0.2
        }
    }),

    // Clutch performer under pressure
    clutch: createGolferProfile({
        id: "preset-clutch",
        name: "The Closer",
        handicapIndex: 5,
        skills: {
            offTheTee: 0.1,
            approach: 0.2,
            aroundGreen: 0.2,
            putting: 0.3
        },
        consistency: 0.95,
        situational: {
            weatherResilience: 0.5,
            pressureHandling: 0.4,
            homeAdvantage: 0.15
        }
    })
};

/**
 * Generate a random golfer with plausible attributes
 */
export const generateRandomGolfer = (handicapRange = [5, 25]) => {
    const handicap = Math.floor(Math.random() * (handicapRange[1] - handicapRange[0] + 1)) + handicapRange[0];

    // Generate skills that roughly balance out (slight positive or negative bias)
    const skillBias = (Math.random() - 0.5) * 0.4;
    const generateSkill = () => (Math.random() - 0.5) * 0.8 + skillBias;

    const skills = {
        offTheTee: generateSkill(),
        approach: generateSkill(),
        aroundGreen: generateSkill(),
        putting: generateSkill()
    };

    // Normalise so skills roughly sum to near zero
    const skillSum = Object.values(skills).reduce((a, b) => a + b, 0);
    const adjustment = skillSum / 4;
    Object.keys(skills).forEach(key => {
        skills[key] = Math.max(-0.8, Math.min(0.8, skills[key] - adjustment));
    });

    return createGolferProfile({
        id: `random-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: generateGolferName(),
        nationality: NATIONALITIES[Math.floor(Math.random() * NATIONALITIES.length)],
        handicapIndex: handicap,
        skills,
        consistency: 0.8 + Math.random() * 0.5,
        situational: {
            weatherResilience: 0.3 + Math.random() * 0.5,
            pressureHandling: (Math.random() - 0.5) * 0.6,
            homeAdvantage: 0.1 + Math.random() * 0.3
        }
    });
};

/**
 * Generate multiple random golfers
 */
export const generateRandomGolfers = (count, handicapRange = [5, 25]) => {
    return Array.from({ length: count }, () => generateRandomGolfer(handicapRange));
};

// Helper: Random golfer names
const FIRST_NAMES = [
    "James", "Robert", "Michael", "William", "David", "Richard", "Thomas", "Charles",
    "Daniel", "Matthew", "Andrew", "Joshua", "Christopher", "Joseph", "Anthony",
    "John", "Mark", "Steven", "Paul", "Brian", "Kevin", "Jason", "Timothy", "Ronald",
    "Edward", "Ryan", "Gary", "Nicholas", "Eric", "Stephen", "Jonathan", "Larry",
    "Scott", "Frank", "Benjamin", "Gregory", "Raymond", "Samuel", "Patrick", "Jack"
];

const LAST_NAMES = [
    "Smith", "Johnson", "Williams", "Brown", "Jones", "Miller", "Davis", "Wilson",
    "Anderson", "Taylor", "Thomas", "Jackson", "White", "Harris", "Martin", "Thompson",
    "Garcia", "Martinez", "Robinson", "Clark", "Rodriguez", "Lewis", "Lee", "Walker",
    "Hall", "Allen", "Young", "King", "Wright", "Scott", "Green", "Baker", "Adams",
    "Nelson", "Hill", "Campbell", "Mitchell", "Roberts", "Carter", "Phillips"
];

const generateGolferName = () => {
    const first = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
    const last = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
    return `${first} ${last}`;
};

const NATIONALITIES = [
    "USA", "GBR", "IRL", "AUS", "RSA", "ESP", "GER", "FRA", "ITA", "SWE",
    "JPN", "KOR", "CAN", "ARG", "MEX", "NZL", "SCO", "WAL", "NOR", "DEN"
];

/**
 * Skill labels for display
 */
export const SKILL_LABELS = {
    offTheTee: "Off the Tee",
    approach: "Approach",
    aroundGreen: "Around the Green",
    putting: "Putting"
};

/**
 * Get skill description based on value
 */
export const getSkillDescription = (value) => {
    if (value >= 0.5) return "Excellent";
    if (value >= 0.25) return "Strong";
    if (value >= 0.1) return "Good";
    if (value >= -0.1) return "Average";
    if (value >= -0.25) return "Below Average";
    if (value >= -0.5) return "Weak";
    return "Poor";
};

/**
 * Get handicap category label
 */
export const getHandicapCategory = (handicap) => {
    if (handicap < 0) return "Plus Handicap";
    if (handicap === 0) return "Scratch";
    if (handicap <= 5) return "Low Single Figure";
    if (handicap <= 9) return "Single Figure";
    if (handicap <= 14) return "Mid Handicapper";
    if (handicap <= 20) return "High Handicapper";
    if (handicap <= 28) return "High Handicapper";
    return "Beginner";
};

/**
 * Calculate overall skill rating (for comparison purposes)
 * Weighted average of skills, accounting for consistency
 */
export const calculateOverallRating = (golfer) => {
    const { skills, consistency, handicapIndex } = golfer;

    // Base rating from handicap (inverted - lower handicap = higher rating)
    const handicapRating = Math.max(0, 100 - handicapIndex * 2.5);

    // Skill modifier (sum of skills, scaled)
    const skillSum = skills.offTheTee + skills.approach + skills.aroundGreen + skills.putting;
    const skillModifier = skillSum * 5;

    // Consistency bonus (lower is better)
    const consistencyBonus = (1 - consistency) * 10;

    return Math.round(Math.max(0, Math.min(100, handicapRating + skillModifier + consistencyBonus)));
};
